import { Icon } from 'app/components/common/Icon';
import { PercentAmpel } from 'app/components/common/PercentAmpel';
import { MonthPicker } from 'app/components/common/DatePickers';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { DigitalReportVM } from 'app/stores/ui/agt.digitalreport.ui.store';
import { formatBM, formatBMToMonthName, formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { runInAction } from 'mobx';
import { init, EChartsOption, ECharts } from 'echarts';
import { debounce } from 'lodash';
import colors from 'assets/scss/colors.module.scss';

export const DigitalreportOptions = observer(() => {
	const { agtDigitalreportUiStore } = useStore();

	useEffect(() => {
		// Only load if not already loaded (bms array is empty)
		if (agtDigitalreportUiStore.bms.length === 0) {
			agtDigitalreportUiStore.load();
		}
	}, [agtDigitalreportUiStore]);

	const onChange = (value: number) => {
		runInAction(() => {
			agtDigitalreportUiStore.maxBmCurrent = value;
			agtDigitalreportUiStore.minBmCurrent = value;
		});
	};

	return <MonthPicker value={agtDigitalreportUiStore.maxBmCurrent} opts={agtDigitalreportUiStore.bmOpts} onChange={onChange} queryParam="bm" />;
});

function useFilteredDigitalReportData() {
	const { agtDigitalReportStore, agenturListUiStore, berichteUiiStore } = useStore();
	const [filtered, setFiltered] = useState<import('app/stores/agt.digitalreport.store').AgtDigitalReportModel[]>([]);

	useEffect(() => {
		agtDigitalReportStore.findAll().then((allData) => {
			const filteredAgtIds = agenturListUiStore.filteredAgtIds;
			let result = allData.filter((d) => filteredAgtIds.includes(d.agtId));
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				result = result.filter((d) => d.agtId === agtId);
			}
			setFiltered(result);
		});
	}, [agtDigitalReportStore, agenturListUiStore.filterHasChanged, berichteUiiStore.selectedAgt]);

	return filtered;
}

const DigitalreportMazAnteilChart = observer(() => {
	const { uiStore } = useStore();
	const chartRef = useRef<HTMLDivElement>(null);
	const filtered = useFilteredDigitalReportData();

	const chartData = useMemo(() => {
		const byBm = new Map<number, { sumAntMaz: number; sumAntMaz1870: number; sumAntMazNg: number; count: number }>();
		for (const d of filtered) {
			const entry = byBm.get(d.bm) || { sumAntMaz: 0, sumAntMaz1870: 0, sumAntMazNg: 0, count: 0 };
			entry.sumAntMaz += d.antMaz;
			entry.sumAntMaz1870 += d.antMaz1870;
			entry.sumAntMazNg += d.antMazNg;
			entry.count++;
			byBm.set(d.bm, entry);
		}
		return Array.from(byBm.entries())
			.map(([bm, v]) => ({
				bm,
				antMaz: v.count > 0 ? Math.round((v.sumAntMaz / v.count) * 100) / 100 : 0,
				antMaz1870: v.count > 0 ? Math.round((v.sumAntMaz1870 / v.count) * 100) / 100 : 0,
				antMazNg: v.count > 0 ? Math.round((v.sumAntMazNg / v.count) * 100) / 100 : 0,
			}))
			.sort((a, b) => a.bm - b.bm);
	}, [filtered]);

	useEffect(() => {
		if (!chartRef.current || chartData.length === 0) return;

		const baseHeight = 75;

		const option: EChartsOption = {
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'line' },
				formatter: (params: any) => {
					if (!Array.isArray(params)) return '';
					const idx = params[0].dataIndex;
					const d = chartData[idx];

					// Use the actual numeric BM from chartData instead of the formatted axis value
					let result = `<strong>${formatBMToMonthName(d.bm)}</strong><br/><br/>`;
					result += `mAZ Gesamt: ${d.antMaz.toFixed(2)}%<br/>`;
					result += `mAZ 18-70: ${d.antMaz1870.toFixed(2)}%<br/>`;
					result += `mAZ Neukunden: ${d.antMazNg.toFixed(2)}%<br/>`;

					const zeBestand = (d.antMaz1870 / 60) * 100;
					const zeNeu = (d.antMazNg / 75) * 100;
					const avgZe = (zeBestand + zeNeu) / 2;
					result += `<br/><strong>ZE: ${avgZe.toFixed(1)}%</strong>`;
					return result;
				},
			},
			legend: {
				data: ['mAZ Gesamt', 'mAZ 18-70', 'mAZ Neukunden', 'ZE'],
				bottom: 0,
			},
			grid: { left: 20, right: 20, top: 50, bottom: 30, containLabel: true },
			labelLayout: {
				hideOverlap: false,
			},
			xAxis: {
				type: 'category',
				boundaryGap: true,
				data: chartData.map((d) => formatBM(d.bm)),
				axisLabel: {
					interval: 0,
					fontSize: 10,
				},
			},
			yAxis: {
				type: 'value',
				min: 0,
				max: 125,
				axisLabel: { show: false },
				splitLine: { show: false },
				axisLine: { show: false },
				axisTick: { show: false },
			},
			series: [
				{
					name: 'mAZ Gesamt',
					type: 'line',
					stack: 'base',
					areaStyle: {},
					lineStyle: { width: 0 },
					symbol: 'none',
					emphasis: { focus: 'series' },
					data: chartData.map((d) => {
						const total = d.antMaz + d.antMaz1870 + d.antMazNg;
						if (total === 0) return 0;
						return (d.antMaz / total) * baseHeight;
					}),
					color: colors.green,
				},
				{
					name: 'mAZ 18-70',
					type: 'line',
					stack: 'base',
					areaStyle: {},
					lineStyle: { width: 0 },
					symbol: 'none',
					emphasis: { focus: 'series' },
					data: chartData.map((d) => {
						const total = d.antMaz + d.antMaz1870 + d.antMazNg;
						if (total === 0) return 0;
						return (d.antMaz1870 / total) * baseHeight;
					}),
					color: colors.yellow,
				},
				{
					name: 'mAZ Neukunden',
					type: 'line',
					stack: 'base',
					areaStyle: {},
					lineStyle: { width: 0 },
					symbol: 'none',
					emphasis: { focus: 'series' },
					data: chartData.map((d) => {
						const total = d.antMaz + d.antMaz1870 + d.antMazNg;
						if (total === 0) return 0;
						return (d.antMazNg / total) * baseHeight;
					}),
					color: colors.purple,
				},
				{
					name: 'ZE',
					type: 'line',
					stack: 'base',
					areaStyle: {},
					lineStyle: { width: 0 },
					symbol: 'none',
					silent: true,
					data: chartData.map((d) => {
						const zeBestand = (d.antMaz1870 / 60) * 100;
						const zeNeu = (d.antMazNg / 75) * 100;
						const avgZe = (zeBestand + zeNeu) / 2;
						return (avgZe / 100) * 50;
					}),
					color: colors.primary,
					markLine: {
						symbol: 'none',
						label: { show: false },
						lineStyle: {
							color: colors.primary,
							type: 'solid',
							width: 2,
						},
						data: [{ yAxis: baseHeight }],
						silent: true,
					},
					markArea: {
						itemStyle: {
							borderColor: colors.border,
							borderWidth: 1,
						},
						label: {
							show: true,
							position: 'top',
							color: colors.grey,
							fontSize: 11,
							align: 'center',
							formatter: (params: any) => {
								// params.dataIndex gives us the index of the markArea in the data array
								const idx = params.dataIndex;
								if (idx >= 0 && idx < chartData.length) {
									const d = chartData[idx];
									const zeBestand = (d.antMaz1870 / 60) * 100;
									const zeNeu = (d.antMazNg / 75) * 100;
									const avgZe = (zeBestand + zeNeu) / 2;
									const monthName = formatBMToMonthName(d.bm);
									return `{bold|${monthName}}\nZE: ${avgZe.toFixed(0)}%`;
								}
								return '';
							},
							rich: {
								bold: {
									fontWeight: 'bold',
								},
							},
						},
						data: chartData.map((_, idx) => [
							{
								xAxis: idx - 0.4,
								yAxis: 0,
							},
							{
								xAxis: idx + 0.4,
								yAxis: 125,
							},
						]),
					},
				},
			],
			animation: !uiStore.printMode,
		};

		const chart: ECharts = init(chartRef.current, null, { renderer: 'svg', height: 400 });
		chart.setOption(option);

		const handleResize = debounce(() => chart.resize(), 300);
		window.addEventListener('resize', handleResize);
		return () => {
			chart.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [chartRef, chartData, uiStore.printMode]);

	return <div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>;
});

const DigitalreportMazChart = observer(() => {
	const { uiStore } = useStore();
	const chartRef = useRef<HTMLDivElement>(null);
	const filtered = useFilteredDigitalReportData();

	const chartData = useMemo(() => {
		const byBm = new Map<number, { anzMaz: number; anzMaz1870: number; anzMazNg: number; sumAntMaz: number; sumAntMaz1870: number; sumAntMazNg: number; count: number }>();
		for (const d of filtered) {
			const entry = byBm.get(d.bm) || { anzMaz: 0, anzMaz1870: 0, anzMazNg: 0, sumAntMaz: 0, sumAntMaz1870: 0, sumAntMazNg: 0, count: 0 };
			entry.anzMaz += d.anzMaz;
			entry.anzMaz1870 += d.anzMaz1870;
			entry.anzMazNg += d.anzMazNg;
			entry.sumAntMaz += d.antMaz;
			entry.sumAntMaz1870 += d.antMaz1870;
			entry.sumAntMazNg += d.antMazNg;
			entry.count++;
			byBm.set(d.bm, entry);
		}
		return Array.from(byBm.entries())
			.map(([bm, v]) => ({
				bm,
				anzMaz: v.anzMaz,
				anzMaz1870: v.anzMaz1870,
				anzMazNg: v.anzMazNg,
				antMaz: v.count > 0 ? Math.round((v.sumAntMaz / v.count) * 100) / 100 : 0,
				antMaz1870: v.count > 0 ? Math.round((v.sumAntMaz1870 / v.count) * 100) / 100 : 0,
				antMazNg: v.count > 0 ? Math.round((v.sumAntMazNg / v.count) * 100) / 100 : 0,
			}))
			.sort((a, b) => a.bm - b.bm);
	}, [filtered]);

	useEffect(() => {
		if (!chartRef.current || chartData.length === 0) return;

		const option: EChartsOption = {
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'cross' },
			},
			legend: {
				data: ['mAZ Gesamt', 'mAZ 18-70', 'mAZ Neukunden', 'Ø mAZ Gesamt %', 'Ø mAZ 18-70 %', 'Ø mAZ Neukunden %'],
				bottom: 0,
			},
			grid: { left: 10, right: 10, top: 10, bottom: 60, containLabel: true },
			xAxis: { type: 'category', data: chartData.map((d) => formatBM(d.bm)) },
			yAxis: [
				{ type: 'value', name: 'Anzahl', position: 'left' },
				{ type: 'value', name: 'Anteil %', position: 'right', max: 100, axisLabel: { formatter: '{value}%' } },
			],
			series: [
				{ name: 'mAZ Gesamt', type: 'bar', yAxisIndex: 0, data: chartData.map((d) => d.anzMaz) },
				{ name: 'mAZ 18-70', type: 'bar', yAxisIndex: 0, data: chartData.map((d) => d.anzMaz1870) },
				{ name: 'mAZ Neukunden', type: 'bar', yAxisIndex: 0, data: chartData.map((d) => d.anzMazNg) },
				{ name: 'Ø mAZ Gesamt %', type: 'line', yAxisIndex: 1, data: chartData.map((d) => d.antMaz) },
				{ name: 'Ø mAZ 18-70 %', type: 'line', yAxisIndex: 1, data: chartData.map((d) => d.antMaz1870) },
				{ name: 'Ø mAZ Neukunden %', type: 'line', yAxisIndex: 1, data: chartData.map((d) => d.antMazNg) },
			],
			animation: !uiStore.printMode,
		};

		const chart: ECharts = init(chartRef.current, null, { renderer: 'svg', height: 400 });
		chart.setOption(option);

		const handleResize = debounce(() => chart.resize(), 300);
		window.addEventListener('resize', handleResize);
		return () => {
			chart.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [chartRef, chartData, uiStore.printMode]);

	return <div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>;
});

export const DigitalreportListe = observer(() => {
	const { agtDigitalreportUiStore, berichteUiiStore, agenturListUiStore } = useStore();

	const tm = useMemo(() => {
		const getAgg = (path: string) => {
			return [
				{
					path,
					format: formatNum,
				},
				{
					path,
					format: formatNum,
					aggType: AggregrationType.avg,
				},
				{
					path,
					format: formatNum,
					aggType: AggregrationType.count,
				},
			];
		};

		const getAggProz = (path: string, yellow: number, green: number) => {
			return [
				{
					path,
					format: formatNum,
					aggType: AggregrationType.avg,
					render: (tm: TableModel<DigitalReportVM, number>, aggValue?: number) => {
						if (!aggValue) return <></>;
						return <PercentAmpel value={aggValue} yellowMin={yellow} greenMin={green} />;
					},
				},
			];
		};

		const tm = new TableModel<DigitalReportVM, number>();
		const cols = [
			{
				label: 'Agentur',
				path: 'agt.name',
				render: AgenturNameCell,
			},
			{
				label: ['Bestand', 'Kunden 18-70 Jahre'],
				path: 'pos.anzKunden18',
				agg: getAgg('pos.anzKunden18'),
			},
			{
				label: 'Digital ID',
				path: 'pos.prozDigiId18',
				agg: getAggProz('pos.prozDigiId18', 60, 85),
				render: (row: TableRow<DigitalReportVM>) => {
					return <PercentAmpel value={row.data.pos.prozDigiId18} yellowMin={60} greenMin={85} />;
				},
			},
			{
				label: 'wEWE',
				path: 'pos.prozWewe18',
				agg: getAggProz('pos.prozWewe18', 35, 60),
				render: (row: TableRow<DigitalReportVM>) => {
					return <PercentAmpel value={row.data.pos.prozWewe18} yellowMin={35} greenMin={60} />;
				},
			},
			{
				label: 'mAZ',
				path: 'pos.prozMaz18',
				agg: getAggProz('pos.prozMaz18', 30, 60),
				render: (row: TableRow<DigitalReportVM>) => {
					return <PercentAmpel value={row.data.pos.prozMaz18} yellowMin={30} greenMin={60} />;
				},
			},
			{
				label: ['Neugeschäft', 'Neukunden'],
				path: 'pos.anzKundenNeu',
				agg: getAgg('pos.anzKundenNeu'),
				cellModifier: 'border-left-blue bg-blue',
				labelModifier: 'border-left-blue bg-blue',
			},
			{
				label: 'Digital ID',
				path: 'pos.prozDigiIdNeu',
				agg: getAggProz('pos.prozDigiIdNeu', 90, 95),
				cellModifier: 'bg-blue',
				labelModifier: 'bg-blue',
				render: (row: TableRow<DigitalReportVM>) => {
					return <PercentAmpel value={row.data.pos.prozDigiIdNeu} yellowMin={90} greenMin={95} />;
				},
			},
			{
				label: 'wEWE',
				path: 'pos.prozWeweNeu',
				agg: getAggProz('pos.prozWeweNeu', 40, 70),
				cellModifier: 'bg-blue',
				labelModifier: 'bg-blue',
				render: (row: TableRow<DigitalReportVM>) => {
					return <PercentAmpel value={row.data.pos.prozWeweNeu} yellowMin={40} greenMin={70} />;
				},
			},
			{
				label: 'mAZ',
				path: 'pos.prozMazNeu',
				agg: getAggProz('pos.prozMazNeu', 50, 75),
				cellModifier: 'bg-blue',
				labelModifier: 'bg-blue',
				render: (row: TableRow<DigitalReportVM>) => {
					return <PercentAmpel value={row.data.pos.prozMazNeu} yellowMin={50} greenMin={75} />;
				},
			},
		];
		tm.setCols(cols);
		tm.sortBy = 'agt.name';
		tm.idProperty = 'id';
		tm.idType = 'number';
		return tm;
	}, []);

	useEffect(() => {
		const bm = agtDigitalreportUiStore.maxBmCurrent;
		if (!bm) return;

		agtDigitalreportUiStore.loadByBm(bm).then((res) => {
			res = agenturListUiStore.applyFilterOnAgt(res) as DigitalReportVM[];
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				res = res.filter((d) => d.agt.agtId === agtId);
			}
			tm.setRowData(res);
		});
	}, [tm, agtDigitalreportUiStore.maxBmCurrent, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged]);
	return (
		<>
			<div className="table-container">
				{/* <DigitalreportMazAnteilChart />
				<DigitalreportMazChart /> */}
				<DigitalreportOptions />
				<Table tm={tm} stickyHeader={true}></Table>

				<div className="pad-1rem">
					<div className="note-box content page-break-avoid">
						<p>
							<Icon iconClass="info-circle" />
						</p>
						<div className="note-message">
							<p>
								<strong>Legende:</strong>
							</p>

							<table className="table">
								<thead>
									<tr>
										<th></th>
										<th>Digital ID</th>
										<th>wEWE</th>
										<th>mAZ</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<strong>Bestand</strong>
										</td>
										<td>
											<span className={`circle color-error`}>&#11044;</span> 0 - 59% <br />
											<span className={`circle color-warning`}>&#11044;</span> 60 - 84% <br />
											<span className={`circle color-success`}>&#11044;</span> &gt; 85% <br />
										</td>
										<td>
											<span className={`circle color-error`}>&#11044;</span> 0 - 34% <br />
											<span className={`circle color-warning`}>&#11044;</span> 35 - 59% <br />
											<span className={`circle color-success`}>&#11044;</span> &gt; 60% <br />
										</td>
										<td>
											<span className={`circle color-error`}>&#11044;</span> 0 - 29% <br />
											<span className={`circle color-warning`}>&#11044;</span> 30 - 59% <br />
											<span className={`circle color-success`}>&#11044;</span> &gt; 60% <br />
										</td>
									</tr>
									<tr>
										<td>
											<strong>Neugeschäft</strong>
										</td>
										<td>
											<span className={`circle color-error`}>&#11044;</span> 0 - 89% <br />
											<span className={`circle color-warning`}>&#11044;</span> 90 - 94% <br />
											<span className={`circle color-success`}>&#11044;</span> &gt; 95% <br />
										</td>
										<td>
											<span className={`circle color-error`}>&#11044;</span> 0 - 39% <br />
											<span className={`circle color-warning`}>&#11044;</span> 40 - 69% <br />
											<span className={`circle color-success`}>&#11044;</span> &gt; 70% <br />
										</td>
										<td>
											<span className={`circle color-error`}>&#11044;</span> 0 - 49% <br />
											<span className={`circle color-warning`}>&#11044;</span> 50 - 74% <br />
											<span className={`circle color-success`}>&#11044;</span> &gt; 75% <br />
										</td>
									</tr>
								</tbody>
							</table>

							<div className="grid is-col-auto pad-top-1rem pad-bottom-1rem">
								<span className={`circle bg-color-error`} />
								<span>
									Ihr Kundenzugang ist noch nicht zukunftsfähig aufgestellt: Sie befinden sich im internen Vergleich unter den Agenturen mit der geringsten Durchdringung im Bestand/
									Neugeschäft. Hier besteht höchster Handlungsdruck, um Ihre Kunden und Kundinnen auch zukünftig persönlich und digital erreichen zu können.
								</span>
							</div>

							<div className="grid is-col-auto pad-bottom-1rem">
								<span className={`circle bg-color-warning`} />
								<span>
									Ihr Kundenzugang ist nur teilweise zukunftsfähig aufgestellt: Die Durchdringung ist im internen Vergleich mit anderen Agenturen leicht überdurchschnittlich. Sie
									erreichen allerdings nur Teile Ihres Bestands persönlich und digital. Bauen Sie die Digitalisierung Ihres Bestands weiter aus, um von den Vorteilen digitaler
									Kundenbeziehungen zu profitieren
								</span>
							</div>

							<div className="grid is-col-auto">
								<span className={`circle bg-color-success`} />
								<span>
									Ihr Kundenzugang ist in weiten Teilen bereits gut für die Zukunft aufgestellt: Im internen Agenturvergleich gehören Sie zu den digitalen Top-Performern. Sie
									erreichen oder übertreffen unser Ambitionsniveau, das sich auch aus externen Benchmarks ableitet. Kundenbeziehungen zu profitieren
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
});
